import { useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { Alert, AlertDescription } from 'src/@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Separator } from 'src/@/components/ui/separator';
import { Switch } from 'src/@/components/ui/switch';
import { Slider } from 'src/@/components/ui/slider';
import { Badge } from 'src/@/components/ui/badge';
import { v2_actionContarProdutos } from 'src/actions/v2/cadastro/v2_actionProdutos';

const LABEL_CLS = 'text-xs font-semibold uppercase tracking-wider text-gray-500';
const HINT_CLS = 'text-xs text-gray-400';
const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

// Precisam espelhar lais-ai/src/agents/ecommerce/identity.js -> DEFAULT_AGENT_CONFIG.
// Aqui sao apenas placeholder/fallback visual: campo vazio significa "usar o
// default do runtime", nunca e gravado.
const DEFAULTS = {
    somenteComEstoque: true,
    limiteBusca: 20,
    maxSugestoes: 3,
    maxTentativasRelax: 3,
    enviarMidia: false,
    ranking: 'cheapest',
    suggestionStyle: 'detailed_balloons',
    productUrlPattern: '',
};

// Espelham DEFAULT_AGENT_CONFIG.buscaPorImagem no lais-ai. Os numeros vieram de
// medicao contra catalogos reais (lais-ai/scripts/verificar-embedding.mjs): o
// piso de ruido entre produtos SEM relacao nenhuma fica em 0,48-0,60, entao um
// corte "conservador" de 0,60 aceitaria o catalogo inteiro.
const DEFAULTS_IMAGEM = {
    ativo: true,
    pisoScore: 0.64,
    gapMinimo: 0.08,
    aoNaoEncontrar: 'pedir_descricao',
};

const coerceInt = (value, min, max) => {
    if (value === '' || value === null || value === undefined) return undefined;
    const n = Number(value);
    if (!Number.isFinite(n)) return undefined;
    const i = Math.trunc(n);
    return Math.max(min, Math.min(max, i));
};

export default function CatalogoBuscaSection({ aiAgent, changeModuleConfig }) {
    const { t } = useTranslation('settings');
    const { cliente } = useAuth();
    const catalogo = aiAgent?.config?.module?.ecommerce?.catalogo || {};

    const [contagem, setContagem] = useState(null);
    const [erroContagem, setErroContagem] = useState(false);

    // A busca por foto depende de "image" em allowsInChat, que fica na aba
    // Configuracoes — nao nesta. Sem esse gate o webhook nem entrega a imagem
    // ao agente, entao ligar a busca por imagem aqui nao teria efeito nenhum.
    const allowsInChat = aiAgent?.config?.generalConfig?.allowsInChat;
    const aceitaImagem = Array.isArray(allowsInChat) && allowsInChat.includes('image');

    useEffect(() => {
        let cancelado = false;
        async function carregar() {
            if (!cliente?._id) return;
            try {
                const resp = await v2_actionContarProdutos(cliente._id);
                if (!cancelado) setContagem(resp?.data?.data ?? null);
            } catch (err) {
                if (!cancelado) setErroContagem(true);
            }
        }
        carregar();
        return () => { cancelado = true; };
    }, [cliente?._id]);

    const updateCatalogo = (patch) => {
        changeModuleConfig({ catalogo: { ...catalogo, ...patch } });
    };

    const busca = catalogo.buscaPorImagem || {};
    const updateBuscaImagem = (patch) => {
        updateCatalogo({ buscaPorImagem: { ...DEFAULTS_IMAGEM, ...busca, ...patch } });
    };

    const naoEncontrarOpcoes = useMemo(
        () => [
            { value: 'pedir_descricao', label: t('aiAgents.ecommerce.buscaImagem.acaoPedirDescricao', 'Pedir que o cliente descreva o produto') },
            { value: 'mostrar_similares', label: t('aiAgents.ecommerce.buscaImagem.acaoSimilares', 'Mostrar produtos parecidos mesmo assim') },
            { value: 'transferir', label: t('aiAgents.ecommerce.buscaImagem.acaoTransferir', 'Encaminhar para o time') },
        ],
        [t],
    );

    const rankingOpcoes = useMemo(
        () => [
            { value: 'cheapest', label: t('aiAgents.ecommerce.catalogo.rankingCheapest', 'Mais baratos primeiro') },
            { value: 'closest_to_budget', label: t('aiAgents.ecommerce.catalogo.rankingClosest', 'Próximos do valor que o cliente informou') },
            { value: 'most_stock', label: t('aiAgents.ecommerce.catalogo.rankingStock', 'Maior estoque primeiro') },
            { value: 'best_seller', label: t('aiAgents.ecommerce.catalogo.rankingBestSeller', 'Mais vendidos primeiro') },
        ],
        [t]
    );

    const styleOpcoes = useMemo(
        () => [
            { value: 'detailed_balloons', label: t('aiAgents.ecommerce.catalogo.styleBalloons', 'Cards com ícones (1 mensagem por produto)') },
            { value: 'card_com_foto', label: t('aiAgents.ecommerce.catalogo.stylePhoto', 'Card com foto do produto') },
            { value: 'compact_inline', label: t('aiAgents.ecommerce.catalogo.styleCompact', 'Lista compacta inline') },
        ],
        [t]
    );

    const semProdutos = contagem && contagem.count === 0;
    const estiloExigeMidia = (catalogo.suggestionStyle || DEFAULTS.suggestionStyle) === 'card_com_foto';

    return (
        <div className="flex flex-col gap-4">
            {/* Origem do catálogo — não existe equivalente no imobiliário, cujo
                catálogo é uma coleção sincronizada. Aqui a fonte é um cadastro
                do próprio painel, então vale mostrar o estado dele. */}
            <Card className="rounded-xl border border-violet-100 bg-violet-50/40">
                <CardContent className="flex items-start gap-3 p-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white shrink-0">
                        <Iconify icon="mdi:package-variant" width={20} className="text-violet-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900">
                            {t('aiAgents.ecommerce.catalogo.origemTitle', 'Origem do catálogo')}
                        </p>
                        <p className="text-xs text-gray-600 mt-0.5">
                            <Trans
                                t={t}
                                i18nKey="aiAgents.ecommerce.catalogo.origemHint"
                                defaults="A assistente busca nos produtos cadastrados em <strong1>Configurações → Cadastro → Produtos</strong1>. Ela só consegue oferecer o que estiver lá."
                                components={{ strong1: <strong /> }}
                            />
                        </p>
                        {contagem && (
                            <p className="text-xs text-gray-500 mt-2">
                                {t('aiAgents.ecommerce.catalogo.origemCount', '{{count}} de {{limit}} produtos cadastrados.', {
                                    count: contagem.count,
                                    limit: contagem.limit,
                                })}
                            </p>
                        )}
                        {erroContagem && (
                            <p className="text-xs text-gray-400 mt-2">
                                {t('aiAgents.ecommerce.catalogo.origemErro', 'Não foi possível carregar a contagem de produtos.')}
                            </p>
                        )}
                    </div>
                </CardContent>
            </Card>

            {semProdutos && (
                <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                    <AlertDescription className="text-xs">
                        {t('aiAgents.ecommerce.catalogo.semProdutos', 'Nenhum produto cadastrado. Enquanto o catálogo estiver vazio, a assistente não tem o que oferecer e vai encaminhar todo mundo para o time.')}
                    </AlertDescription>
                </Alert>
            )}

            <Card className="rounded-xl border border-gray-100 bg-white">
                <CardHeader>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                            <Iconify icon="mdi:magnify" width={20} className="text-violet-600" />
                        </div>
                        <div>
                            <CardTitle>{t('aiAgents.ecommerce.catalogo.title', 'Busca no catálogo')}</CardTitle>
                            <CardDescription>{t('aiAgents.ecommerce.catalogo.subtitle', 'Como a assistente procura produtos e apresenta as opções ao cliente.')}</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="flex flex-col gap-5">
                    <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50/60">
                        <div>
                            <p className="text-sm font-medium text-gray-800">{t('aiAgents.ecommerce.catalogo.estoqueLabel', 'Oferecer apenas produtos com estoque')}</p>
                            <p className={HINT_CLS}>{t('aiAgents.ecommerce.catalogo.estoqueHint', 'Desligado, a assistente pode sugerir item zerado e gerar frustração.')}</p>
                        </div>
                        <Switch
                            checked={catalogo.somenteComEstoque ?? DEFAULTS.somenteComEstoque}
                            onCheckedChange={(v) => updateCatalogo({ somenteComEstoque: v })}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                            <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.catalogo.limiteLabel', 'Limite da busca no banco')}</Label>
                            <Input
                                type="number"
                                min={1}
                                max={100}
                                value={catalogo.limiteBusca ?? ''}
                                onChange={(e) => updateCatalogo({ limiteBusca: coerceInt(e.target.value, 1, 100) })}
                                placeholder={String(DEFAULTS.limiteBusca)}
                                className={INPUT_CLS}
                            />
                            <p className={HINT_CLS}>{t('aiAgents.ecommerce.catalogo.limiteHint', 'Máximo de produtos que a query devolve. Padrão: {{value}}.', { value: DEFAULTS.limiteBusca })}</p>
                        </div>

                        <div className="flex flex-col gap-2">
                            <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.catalogo.maxSugestoesLabel', 'Produtos por resposta')}</Label>
                            <Input
                                type="number"
                                min={1}
                                max={10}
                                value={catalogo.maxSugestoes ?? ''}
                                onChange={(e) => updateCatalogo({ maxSugestoes: coerceInt(e.target.value, 1, 10) })}
                                placeholder={String(DEFAULTS.maxSugestoes)}
                                className={INPUT_CLS}
                            />
                            <p className={HINT_CLS}>{t('aiAgents.ecommerce.catalogo.maxSugestoesHint', 'Quantos produtos a assistente mostra por rodada. Padrão: {{value}}.', { value: DEFAULTS.maxSugestoes })}</p>
                        </div>

                        <div className="flex flex-col gap-2">
                            <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.catalogo.relaxLabel', 'Tentativas de relaxamento')}</Label>
                            <Input
                                type="number"
                                min={1}
                                max={10}
                                value={catalogo.maxTentativasRelax ?? ''}
                                onChange={(e) => updateCatalogo({ maxTentativasRelax: coerceInt(e.target.value, 1, 10) })}
                                placeholder={String(DEFAULTS.maxTentativasRelax)}
                                className={INPUT_CLS}
                            />
                            <p className={HINT_CLS}>{t('aiAgents.ecommerce.catalogo.relaxHint', 'Quantas vezes ela afrouxa os filtros antes de admitir que não achou. Padrão: {{value}}.', { value: DEFAULTS.maxTentativasRelax })}</p>
                        </div>

                        <div className="flex flex-col gap-2">
                            <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.catalogo.rankingLabel', 'Como ordenar os produtos sugeridos')}</Label>
                            <Select
                                value={catalogo.ranking || DEFAULTS.ranking}
                                onValueChange={(value) => updateCatalogo({ ranking: value })}
                            >
                                <SelectTrigger className={INPUT_CLS}>
                                    <SelectValue placeholder={t('aiAgents.ecommerce.catalogo.selectPlaceholder', 'Selecione')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {rankingOpcoes.map((o) => (
                                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.catalogo.styleLabel', 'Estilo das mensagens de sugestão')}</Label>
                        <Select
                            value={catalogo.suggestionStyle || DEFAULTS.suggestionStyle}
                            onValueChange={(value) => updateCatalogo({ suggestionStyle: value })}
                        >
                            <SelectTrigger className={INPUT_CLS}>
                                <SelectValue placeholder={t('aiAgents.ecommerce.catalogo.selectPlaceholder', 'Selecione')} />
                            </SelectTrigger>
                            <SelectContent>
                                {styleOpcoes.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <Separator className="bg-gray-200/60" />

                    <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50/60">
                            <div>
                                <p className="text-sm font-medium text-gray-800">{t('aiAgents.ecommerce.catalogo.midiaLabel', 'Enviar foto do produto')}</p>
                                <p className={HINT_CLS}>{t('aiAgents.ecommerce.catalogo.midiaHint', 'Manda a imagem cadastrada junto da sugestão, em vez de só o link.')}</p>
                            </div>
                            <Switch
                                aria-label={t('aiAgents.ecommerce.catalogo.midiaLabel', 'Enviar foto do produto')}
                                checked={catalogo.enviarMidia ?? DEFAULTS.enviarMidia}
                                onCheckedChange={(v) => updateCatalogo({ enviarMidia: v })}
                            />
                        </div>

                        {(catalogo.enviarMidia || estiloExigeMidia) && (
                            <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                                <AlertDescription className="text-xs">
                                    {t('aiAgents.ecommerce.catalogo.midiaAviso', 'O envio de imagem pela assistente ainda não está disponível em todos os canais: hoje o fluxo de resposta despacha apenas texto e áudio. Enquanto isso, a foto pode não chegar ao cliente.')}
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.catalogo.urlLabel', 'URL pública do produto')}</Label>
                        <Input
                            value={catalogo.productUrlPattern ?? ''}
                            onChange={(e) => updateCatalogo({ productUrlPattern: e.target.value })}
                            placeholder="https://minhaloja.com.br/produto/[SKU]"
                            className={INPUT_CLS}
                        />
                        <p className={HINT_CLS}>
                            <Trans
                                t={t}
                                i18nKey="aiAgents.ecommerce.catalogo.urlHint"
                                defaults="Usado só quando o produto não tem link próprio cadastrado — o link do produto sempre vence. Placeholders: <code1>[SKU]</code1> e <code2>[ID]</code2>."
                                components={{ code1: <code />, code2: <code /> }}
                            />
                        </p>
                    </div>

                </CardContent>
            </Card>

            {/* ─── Busca por foto ─────────────────────────────────── */}
            <Card className="rounded-xl border border-gray-100 bg-white">
                <CardHeader>
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
                            <Iconify icon="mdi:image-search-outline" width={20} className="text-violet-600" />
                        </div>
                        <div>
                            <CardTitle>{t('aiAgents.ecommerce.buscaImagem.title', 'Busca por foto')}</CardTitle>
                            <CardDescription>
                                {t('aiAgents.ecommerce.buscaImagem.subtitle', 'O cliente manda a foto de um produto e a assistente procura o equivalente no seu catálogo.')}
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>

                <CardContent className="flex flex-col gap-5">
                    {!aceitaImagem && (
                        <Alert className="bg-blue-50 border-blue-200 text-blue-800">
                            <AlertDescription className="text-xs">
                                {t('aiAgents.ecommerce.catalogo.avisoAllowsInChat', 'Para o cliente poder mandar a foto de um produto, marque "image" em Conhecimento do Agente, na aba Configurações. Sem isso, imagens recebidas não chegam à assistente.')}
                            </AlertDescription>
                        </Alert>
                    )}

                    <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50/60">
                        <div>
                            <p className="text-sm font-medium text-gray-800">
                                {t('aiAgents.ecommerce.buscaImagem.ativoLabel', 'Procurar produto pela foto do cliente')}
                            </p>
                            <p className={HINT_CLS}>
                                {t('aiAgents.ecommerce.buscaImagem.ativoHint', 'A assistente identifica o que aparece na foto e compara com as fotos dos seus produtos.')}
                            </p>
                        </div>
                        <Switch
                            aria-label={t('aiAgents.ecommerce.buscaImagem.ativoLabel', 'Procurar produto pela foto do cliente')}
                            checked={busca.ativo ?? DEFAULTS_IMAGEM.ativo}
                            disabled={!aceitaImagem}
                            onCheckedChange={(v) => updateBuscaImagem({ ativo: v })}
                        />
                    </div>

                    {/* Cobertura: produto sem foto cadastrada NÃO pode ser achado por foto.
                        É a frustração mais previsível desta funcionalidade, então o número
                        aparece antes de o lojista ligar o switch e não depois de reclamar. */}
                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.buscaImagem.coberturaLabel', 'Cobertura do catálogo')}</Label>
                        {contagem === null ? (
                            <p className={HINT_CLS}>{t('aiAgents.ecommerce.buscaImagem.coberturaCarregando', 'Verificando...')}</p>
                        ) : (
                            <>
                                <div className="flex flex-wrap items-center gap-2">
                                    <Badge variant="outline" className="text-xs font-medium flex items-center gap-1.5 bg-gray-50 text-gray-700 border-gray-200">
                                        <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                                        {t('aiAgents.ecommerce.buscaImagem.coberturaTotal', '{{n}} produtos ativos', { n: contagem.count ?? 0 })}
                                    </Badge>
                                    <Badge variant="outline" className={`text-xs font-medium flex items-center gap-1.5 ${(contagem.comFoto ?? 0) > 0 ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${(contagem.comFoto ?? 0) > 0 ? 'bg-blue-500' : 'bg-amber-500'}`} />
                                        {t('aiAgents.ecommerce.buscaImagem.coberturaComFoto', '{{n}} com foto cadastrada', { n: contagem.comFoto ?? 0 })}
                                    </Badge>
                                    <Badge variant="outline" className={`text-xs font-medium flex items-center gap-1.5 ${(contagem.indexados ?? 0) > 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${(contagem.indexados ?? 0) > 0 ? 'bg-green-500' : 'bg-amber-500'}`} />
                                        {t('aiAgents.ecommerce.buscaImagem.coberturaIndexados', '{{n}} prontos para busca por foto', { n: contagem.indexados ?? 0 })}
                                    </Badge>
                                </div>
                                {(contagem.indexados ?? 0) === 0 && (
                                    <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                                        <AlertDescription className="text-xs">
                                            {(contagem.comFoto ?? 0) === 0
                                                ? t('aiAgents.ecommerce.buscaImagem.avisoSemFotos', 'Nenhum produto tem foto cadastrada. A assistente até entende o que o cliente mandou, mas não tem com o que comparar — ela vai pedir uma descrição em vez de sugerir. Cadastre as fotos em Cadastro › Produtos.')
                                                : t('aiAgents.ecommerce.buscaImagem.avisoNaoIndexado', 'Há fotos cadastradas, mas nenhuma foi preparada para busca ainda. Isso acontece automaticamente em segundo plano; se persistir, fale com o suporte.')}
                                        </AlertDescription>
                                    </Alert>
                                )}
                                {(contagem.indexados ?? 0) > 0 && (contagem.indexados ?? 0) < (contagem.comFoto ?? 0) && (
                                    <p className={HINT_CLS}>
                                        {t('aiAgents.ecommerce.buscaImagem.coberturaParcial', 'Fotos que são logotipo, banner ou imagem genérica ficam de fora de propósito: elas se parecem com tudo e atrapalhariam o resultado.')}
                                    </p>
                                )}
                            </>
                        )}
                    </div>

                    <Separator className="bg-gray-200/60" />

                    {/* Os dois controles abaixo mudam quando a assistente AFIRMA que achou.
                        Os valores padrão vêm de medição contra catálogos reais, não de
                        intuição — mexer neles sem medir costuma piorar. */}
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                            <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.buscaImagem.pisoLabel', 'Exigência de semelhança')}</Label>
                            <span className="text-xs font-mono text-gray-600">{(busca.pisoScore ?? DEFAULTS_IMAGEM.pisoScore).toFixed(2)}</span>
                        </div>
                        <Slider
                            min={0.3}
                            max={0.95}
                            step={0.01}
                            value={[busca.pisoScore ?? DEFAULTS_IMAGEM.pisoScore]}
                            onValueChange={([v]) => updateBuscaImagem({ pisoScore: v })}
                            disabled={!aceitaImagem}
                        />
                        <p className={HINT_CLS}>
                            {t('aiAgents.ecommerce.buscaImagem.pisoHint', 'Quanto o produto precisa parecer com a foto para entrar na resposta. Mais alto sugere menos e erra menos; mais baixo sugere mais e erra mais.')}
                        </p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                            <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.buscaImagem.gapLabel', 'Margem para afirmar')}</Label>
                            <span className="text-xs font-mono text-gray-600">{(busca.gapMinimo ?? DEFAULTS_IMAGEM.gapMinimo).toFixed(2)}</span>
                        </div>
                        <Slider
                            min={0.01}
                            max={0.5}
                            step={0.01}
                            value={[busca.gapMinimo ?? DEFAULTS_IMAGEM.gapMinimo]}
                            onValueChange={([v]) => updateBuscaImagem({ gapMinimo: v })}
                            disabled={!aceitaImagem}
                        />
                        <p className={HINT_CLS}>
                            {t('aiAgents.ecommerce.buscaImagem.gapHint', 'O quanto o primeiro colocado precisa se destacar do segundo para a assistente dizer "é este". Sem essa folga ela apresenta as opções e deixa o cliente escolher.')}
                        </p>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className={LABEL_CLS}>{t('aiAgents.ecommerce.buscaImagem.naoEncontrarLabel', 'Quando não encontra nada parecido')}</Label>
                        <Select
                            value={busca.aoNaoEncontrar ?? DEFAULTS_IMAGEM.aoNaoEncontrar}
                            onValueChange={(v) => updateBuscaImagem({ aoNaoEncontrar: v })}
                            disabled={!aceitaImagem}
                        >
                            <SelectTrigger className={INPUT_CLS}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {naoEncontrarOpcoes.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
